import React, { useState } from 'react';
import type { FormType } from '../types';

interface FormSelectorProps {
  onFormTypeSelected: (formType: FormType) => void;
  onCancel?: () => void;
}

export const FormSelector: React.FC<FormSelectorProps> = ({
  onFormTypeSelected,
  onCancel,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isUSPerson, setIsUSPerson] = useState<boolean | null>(null);
  const [isIndividual, setIsIndividual] = useState<boolean | null>(null);

  const handleUSPersonResponse = (response: boolean) => {
    setIsUSPerson(response);
    if (response) {
      // U.S. person → W-9 form
      setStep(3);
    } else {
      // Non-U.S. person → Ask if individual
      setStep(2);
    }
  };

  const handleIndividualResponse = (response: boolean) => {
    setIsIndividual(response);
    setStep(3);
  };

  const handleConfirm = () => {
    if (isUSPerson) {
      onFormTypeSelected('w9');
    } else if (isIndividual) {
      onFormTypeSelected('w8ben');
    } else {
      // Entity - not supported yet, default to W-8 BEN for now
      // TODO: Add W-8 BEN-E support
      alert('W-8 BEN-E for entities is not yet supported. Please contact support.');
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(isUSPerson ? 1 : 2);
    } else if (step === 2) {
      setStep(1);
      setIsUSPerson(null);
    }
  };

  return (
    <div className="w9-form-selector">
      {/* Step 1: U.S. Person? */}
      {step === 1 && (
        <div className="w9-selector-question">
          <h3 className="w9-question-title">
            Are you a U.S. citizen or U.S. resident for tax purposes?
          </h3>
          <p className="w9-question-help">
            This includes U.S. citizens, green card holders, and those who meet the substantial presence test.
          </p>

          <div className="w9-question-actions">
            <button
              type="button"
              className="w9-btn w9-btn-primary"
              onClick={() => handleUSPersonResponse(true)}
            >
              Yes, I am a U.S. person
            </button>
            <button
              type="button"
              className="w9-btn w9-btn-secondary"
              onClick={() => handleUSPersonResponse(false)}
            >
              No, I am not a U.S. person
            </button>
          </div>

          <div className="w9-question-tooltip">
            <button type="button" className="w9-tooltip-trigger">
              Why are you asking this? ℹ️
            </button>
            <div className="w9-tooltip-content">
              <p>
                The IRS requires different tax forms based on your tax residency status:
              </p>
              <ul>
                <li><strong>U.S. persons</strong> complete Form W-9</li>
                <li><strong>Foreign persons</strong> complete Form W-8 BEN (individuals) or W-8 BEN-E (entities)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Individual or Entity? */}
      {step === 2 && (
        <div className="w9-selector-question">
          <h3 className="w9-question-title">
            Are you an individual or a business entity?
          </h3>
          <p className="w9-question-help">
            Select whether you're completing this form as an individual person or on behalf of a business/organization.
          </p>

          <div className="w9-question-actions">
            <button
              type="button"
              className="w9-btn w9-btn-primary"
              onClick={() => handleIndividualResponse(true)}
            >
              Individual Person
            </button>
            <button
              type="button"
              className="w9-btn w9-btn-secondary"
              onClick={() => handleIndividualResponse(false)}
            >
              Business/Entity
            </button>
          </div>

          <div className="w9-question-nav">
            <button
              type="button"
              className="w9-btn w9-btn-text"
              onClick={handleBack}
            >
              ← Back
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && (
        <div className="w9-selector-confirmation">
          <div className="w9-confirmation-icon">✓</div>
          <h3 className="w9-confirmation-title">
            You'll complete Form {isUSPerson ? 'W-9' : 'W-8 BEN'}
          </h3>

          <div className="w9-confirmation-details">
            <div className="w9-detail-card">
              <h4>Based on your answers:</h4>
              <ul>
                <li>
                  <strong>Tax Status:</strong> {isUSPerson ? 'U.S. Person' : 'Foreign Person'}
                </li>
                {!isUSPerson && (
                  <li>
                    <strong>Type:</strong> {isIndividual ? 'Individual' : 'Business Entity'}
                  </li>
                )}
              </ul>
            </div>

            <div className="w9-detail-card w9-detail-card-info">
              <h4>What to expect:</h4>
              {isUSPerson ? (
                <ul>
                  <li>Provide your legal name and business information</li>
                  <li>Enter your Tax Identification Number (SSN or EIN)</li>
                  <li>Confirm your address and tax classification</li>
                  <li>Sign electronically under penalty of perjury</li>
                </ul>
              ) : (
                <ul>
                  <li>Provide identification and residence information</li>
                  <li>Enter tax identification numbers (if applicable)</li>
                  <li>Claim tax treaty benefits (optional)</li>
                  <li>Certify your foreign status</li>
                </ul>
              )}
            </div>
          </div>

          <div className="w9-confirmation-actions">
            <button
              type="button"
              className="w9-btn w9-btn-primary w9-btn-large"
              onClick={handleConfirm}
            >
              Continue to Form {isUSPerson ? 'W-9' : 'W-8 BEN'} →
            </button>
            <button
              type="button"
              className="w9-btn w9-btn-text"
              onClick={handleBack}
            >
              ← Change Answers
            </button>
          </div>
        </div>
      )}

      {onCancel && (
        <div className="w9-selector-footer">
          <button
            type="button"
            className="w9-btn w9-btn-text w9-btn-sm"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default FormSelector;
